import { create } from 'zustand'
import api from '../services/api'

export interface MaiaMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  ui_actions?: UIAction[]
}

export interface UIAction {
  type: 'navigate' | 'toast' | 'refresh'
  path?: string
  message?: string
}

interface MaiaState {
  isOpen: boolean
  messages: MaiaMessage[]
  isLoading: boolean
  voiceMode: boolean
  isRecording: boolean
  isSpeaking: boolean
  wakeWordEnabled: boolean
  wakeWordActive: boolean  // true quando il listener wake word è effettivamente in ascolto
  autoStartRecording: boolean
  currentPage: string
  micPermission: 'unknown' | 'granted' | 'denied' | 'prompt' | 'insecure'
  lastResponseHadTools: boolean  // true se l'ultima risposta ha usato tool → auto-clear al prossimo invio

  toggle: () => void
  open: () => void
  close: () => void
  openWithVoice: () => void
  setCurrentPage: (page: string) => void
  setVoiceMode: (on: boolean) => void
  setRecording: (on: boolean) => void
  setSpeaking: (on: boolean) => void
  setWakeWordEnabled: (on: boolean) => void
  setWakeWordActive: (on: boolean) => void
  setAutoStartRecording: (on: boolean) => void
  requestMicPermission: () => Promise<boolean>
  checkMicPermission: () => Promise<void>
  sendMessage: (text: string) => Promise<UIAction[]>
  clearMessages: () => void
}

const STORAGE_KEY = 'elettra_messages'
const WAKE_WORD_KEY = 'elettra_wake_word'
const MAX_STORED = 20

function loadMessages(): MaiaMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.slice(-MAX_STORED) : []
    }
  } catch { /* ignore */ }
  return []
}

function saveMessages(messages: MaiaMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED)))
  } catch { /* ignore */ }
}

export const useMaiaStore = create<MaiaState>((set, get) => ({
  isOpen: false,
  messages: loadMessages(),
  isLoading: false,
  voiceMode: false,
  isRecording: false,
  isSpeaking: false,
  wakeWordEnabled: localStorage.getItem(WAKE_WORD_KEY) === 'true',
  wakeWordActive: false,
  autoStartRecording: false,
  currentPage: '',
  micPermission: 'unknown',
  lastResponseHadTools: false,

  toggle: () => set(s => ({ isOpen: !s.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  openWithVoice: () => set({ isOpen: true, voiceMode: true, autoStartRecording: true }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setVoiceMode: (on) => set({ voiceMode: on }),
  setRecording: (on) => set({ isRecording: on }),
  setSpeaking: (on) => set({ isSpeaking: on }),
  setWakeWordEnabled: (on) => {
    localStorage.setItem(WAKE_WORD_KEY, on ? 'true' : 'false')
    set({ wakeWordEnabled: on })
  },
  setWakeWordActive: (on) => set({ wakeWordActive: on }),
  setAutoStartRecording: (on) => set({ autoStartRecording: on }),

  checkMicPermission: async () => {
    // getUserMedia richiede HTTPS (o localhost)
    if (!window.isSecureContext) {
      set({ micPermission: 'insecure' })
      return
    }
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      set({ micPermission: result.state as any })
      if (result.state === 'granted') {
        if (!get().wakeWordEnabled) {
          localStorage.setItem(WAKE_WORD_KEY, 'true')
          set({ wakeWordEnabled: true })
        }
      } else if (result.state === 'prompt' && get().wakeWordEnabled) {
        // Wake word attivo da sessione precedente ma permesso non ancora concesso su questo dominio.
        // Richiedi automaticamente per far funzionare il wake word.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          stream.getTracks().forEach(t => t.stop())
          localStorage.setItem(WAKE_WORD_KEY, 'true')
          set({ micPermission: 'granted', wakeWordEnabled: true })
        } catch {
          // Utente ha rifiutato il prompt
          localStorage.setItem(WAKE_WORD_KEY, 'false')
          set({ micPermission: 'denied', wakeWordEnabled: false })
        }
      }
      result.onchange = () => {
        set({ micPermission: result.state as any })
        if (result.state === 'granted') {
          localStorage.setItem(WAKE_WORD_KEY, 'true')
          set({ wakeWordEnabled: true })
        } else if (result.state === 'denied') {
          localStorage.setItem(WAKE_WORD_KEY, 'false')
          set({ wakeWordEnabled: false })
        }
      }
    } catch {
      // permissions.query non supportato — prova getUserMedia direttamente se wake word attivo
      if (get().wakeWordEnabled) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          stream.getTracks().forEach(t => t.stop())
          set({ micPermission: 'granted' })
        } catch {
          set({ micPermission: 'unknown' })
        }
      } else {
        set({ micPermission: 'unknown' })
      }
    }
  },

  requestMicPermission: async () => {
    if (!window.isSecureContext) {
      set({ micPermission: 'insecure' })
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(t => t.stop())
      localStorage.setItem(WAKE_WORD_KEY, 'true')
      set({ micPermission: 'granted', wakeWordEnabled: true })
      return true
    } catch {
      set({ micPermission: 'denied', wakeWordEnabled: false })
      return false
    }
  },

  sendMessage: async (text: string) => {
    const { messages, voiceMode, currentPage, lastResponseHadTools } = get()

    // Auto-clear: se la risposta precedente ha usato tool, pulisci prima del nuovo messaggio
    // L'utente ha avuto tempo di leggere, ora ELETTRA riparte fresca
    const baseMessages = lastResponseHadTools ? [] : messages

    // Aggiungi messaggio utente
    const userMsg: MaiaMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    }
    const updatedMessages = [...baseMessages, userMsg]
    set({ messages: updatedMessages, isLoading: true, lastResponseHadTools: false })
    saveMessages(updatedMessages)

    // Prepara history per il backend (ultimi 18 msg)
    const history = updatedMessages.slice(-18).map(m => ({
      role: m.role,
      content: m.content,
    }))
    // Rimuovi l'ultimo (è il messaggio corrente, lo mandiamo in "message")
    history.pop()

    try {
      const res = await api.post('/v1/elettra/chat', {
        message: text,
        history,
        voice_mode: voiceMode,
        current_page: currentPage,
      })

      const data = res.data
      const assistantMsg: MaiaMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.text,
        timestamp: Date.now(),
        ui_actions: data.ui_actions,
      }

      const finalMessages = [...updatedMessages, assistantMsg]
      const hadTools = (data.tools_used || 0) > 0
      set({ messages: finalMessages, isLoading: false, lastResponseHadTools: hadTools })
      saveMessages(finalMessages)

      return data.ui_actions || []
    } catch (err: any) {
      const errorText = err.response?.data?.detail || 'Errore di comunicazione con ELETTRA'
      const errorMsg: MaiaMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `⚠️ ${errorText}`,
        timestamp: Date.now(),
      }
      const finalMessages = [...updatedMessages, errorMsg]
      set({ messages: finalMessages, isLoading: false, lastResponseHadTools: false })
      saveMessages(finalMessages)
      return []
    }
  },

  clearMessages: () => {
    set({ messages: [] })
    localStorage.removeItem(STORAGE_KEY)
  },
}))
